import React, { useEffect, useRef, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { useRouter } from 'expo-router';
import { Trans } from '@lingui/react/macro';
import { msg } from '@lingui/core/macro';
import { useSession } from '../../lib/SessionContext';
import { canAccessMainApp } from '../../utils/authHelpers';
import { fetchResumableOnboardingIntents } from '../../lib/onboardingResumption';
import { resumableActivationDestination, type ResumableOnboardingIntent } from '../../lib/onboardingResumptionContract';
import { resolveProfileCompletionDestination } from '../../lib/profileCompletion';
import AppRouteBootScreen from '../auth/AppRouteBootScreen';
import FullPageSignInGate from '../auth/FullPageSignInGate';
import colors from '../../lib/colors';

type Lookup = { userId: string; status: 'loading' | 'error' | 'ready'; intents: ResumableOnboardingIntent[] };

/** Owns only generic-entry routing. Exact activation keeps finalization ownership. */
export default function OnboardingResumptionGate({ children, fallback = '/dashboard' }: {
    children?: React.ReactNode;
    fallback?: string;
}) {
    const { session, isLoading } = useSession();
    const userId = canAccessMainApp(session) ? session?.user.id : undefined;
    const router = useRouter();
    const [attempt, setAttempt] = useState(0);
    const [lookup, setLookup] = useState<Lookup | null>(null);
    const navigating = useRef<string | null>(null);

    useEffect(() => {
        navigating.current = null;
        if (isLoading || !userId) return;
        const controller = new AbortController();
        setLookup({ userId, status: 'loading', intents: [] });
        fetchResumableOnboardingIntents(userId, controller.signal).then((intents) => {
            if (!controller.signal.aborted) setLookup({ userId, status: 'ready', intents });
        }).catch(() => {
            if (!controller.signal.aborted) setLookup({ userId, status: 'error', intents: [] });
        });
        return () => controller.abort();
    }, [attempt, isLoading, userId]);

    const current = lookup?.userId === userId ? lookup : null;
    const emptyDestination = resolveProfileCompletionDestination({ session, destination: fallback }).destination;
    const destination = current?.status === 'ready'
        ? current.intents.length === 1 ? resumableActivationDestination(current.intents[0])
            : current.intents.length === 0 && (!children || emptyDestination !== fallback) ? emptyDestination : null
        : null;

    useEffect(() => {
        if (isLoading || !userId || !destination || navigating.current === destination) return;
        navigating.current = destination;
        router.replace(destination as any);
    }, [destination, isLoading, router, userId]);

    if (!isLoading && !userId) return <FullPageSignInGate redirectTo="/resume-project" />;
    if (isLoading || !current || current.status === 'loading' || destination) {
        return <AppRouteBootScreen message={msg({ id: 'onboarding.resume.checking', message: 'Checking your saved projects…' })} />;
    }
    if (current.status === 'ready' && current.intents.length === 0) return <>{children}</>;

    return <ScrollView contentContainerStyle={styles.container}>
        <View style={styles.card} testID="onboarding-resumption">
            {current.status === 'error' ? <>
                <Text accessibilityRole="header" style={styles.title}><Trans id="onboarding.resume.error">We could not check your saved projects.</Trans></Text>
                <Text accessibilityRole="alert" style={styles.body}><Trans id="onboarding.resume.retryHelp">Your work has not been changed. Try again to continue.</Trans></Text>
                <Pressable accessibilityRole="button" style={styles.button} onPress={() => setAttempt((value) => value + 1)}>
                    <Text style={styles.buttonText}><Trans id="onboarding.resume.retry">Try again</Trans></Text>
                </Pressable>
            </> : <>
                <Text accessibilityRole="header" style={styles.title}><Trans id="onboarding.resume.choose">Which saved project would you like to continue?</Trans></Text>
                <Text style={styles.body}><Trans id="onboarding.resume.chooseHelp">Choose the research topic you want to finish setting up.</Trans></Text>
                {current.intents.map((intent) => <Pressable key={intent.intentId} accessibilityRole="button"
                    style={styles.project} onPress={() => {
                        if (navigating.current) return;
                        navigating.current = resumableActivationDestination(intent);
                        router.replace(navigating.current as any);
                    }}>
                    <Text style={styles.projectTitle}>{intent.projectName}</Text>
                    <Text style={styles.continue}><Trans id="onboarding.resume.continue">Continue setup</Trans></Text>
                </Pressable>)}
            </>}
        </View>
    </ScrollView>;
}

const styles = StyleSheet.create({
    container: { flexGrow: 1, justifyContent: 'center', alignItems: 'center', padding: 24, backgroundColor: colors.bgWeak },
    card: { width: '100%', maxWidth: 560, padding: 24, borderRadius: 20, backgroundColor: colors.card, gap: 18 },
    title: { fontSize: 26, fontWeight: '700', color: colors.text900 },
    body: { fontSize: 16, lineHeight: 24, color: colors.text700 },
    button: { minHeight: 48, padding: 14, borderRadius: 10, backgroundColor: colors.primaryGreen, alignItems: 'center' },
    buttonText: { color: colors.card, fontSize: 16, fontWeight: '600' },
    project: { padding: 18, gap: 10, borderWidth: 1, borderColor: colors.border, borderRadius: 12, minHeight: 72 },
    projectTitle: { fontSize: 18, fontWeight: '600', color: colors.text900 },
    continue: { fontSize: 16, color: colors.primaryGreenDark, fontWeight: '600' },
});
